/* eslint-disable no-underscore-dangle */
import 'features/lots/components/dangerButton.scss';

import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { LotKinds, LotStatus } from '@parallel-fluidics/shared/constants';
import PPartDisplay from 'components/Projects/pPartDisplay';
import LotDisplay from 'features/lots/components/LotDisplay';
import LotKindBadge from 'features/lots/components/LotKindBadge';
import LotStatusBadge from 'features/lots/components/LotStatusBadge';
import QuarantineLotModal from 'features/lots/components/QuarantineLotModal';
import useLot from 'features/lots/hooks/useLot';
import useLotInspections from 'features/lots/hooks/useLotInspections';
import { useInvalidateLotsQuery } from 'features/lots/hooks/useLotsQuery';
import useLotsUsingSource from 'features/lots/hooks/useLotsUsingSource';
import useRealtimeLotEvents from 'features/lots/hooks/useRealtimeLotEvents';
import useTravelersUsingLot from 'features/lots/hooks/useTravelersUsingLot';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdjustLotQtyModal from './components/AdjustLotQtyModal';
import LotEventsTable from './components/LotEventsTable';
import LotInspectionsCard from './components/LotInspectionsCard';
import LotStatusActionModal from './components/LotStatusActionModal';
import PrintLotLabelModal from './components/PrintLotLabelModal';
import RecordLotInspectionModal from './components/RecordLotInspectionModal';

function ageInDays(createdTimestamp) {
  const created = createdTimestamp?.toDate?.();
  if (!created) return '—';
  const days = Math.floor((Date.now() - created.getTime()) / (24 * 60 * 60 * 1000));
  return `${days}d`;
}

export default function LotDetailPage() {
  const { lotId } = useParams();
  const navigate = useNavigate();
  const [activeAction, setActiveAction] = useState(null);

  const {
    data: lot, isLoading, error, invalidate: invalidateLot,
  } = useLot(lotId);
  const invalidateLotsList = useInvalidateLotsQuery();
  const {
    data: inspections = [],
    invalidate: invalidateInspections,
  } = useLotInspections(lotId);
  const { data: usedInLots = [] } = useLotsUsingSource(lotId);
  const { data: usedInTravelers = [] } = useTravelersUsingLot(lotId);
  const { data: events, status: eventsStatus } = useRealtimeLotEvents(lotId);

  if (isLoading) return <Spinner size="large" />;
  if (error || !lot) {
    return <Alert type="error">{error?.message || 'Lot not found'}</Alert>;
  }

  // Refetch the mutated lot everywhere it renders; the Activity table is
  // realtime and updates on its own.
  const handleActionSuccess = () => {
    invalidateLot();
    invalidateLotsList();
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => navigate(`/lots/trace?lotId=${lot.id}`)}>Trace</Button>
                <Button onClick={() => setActiveAction('printLabel')}>Print label</Button>
                <Button onClick={() => setActiveAction('adjust')}>Adjust qty</Button>
                <Button
                  // Quarantined lots keep their status — Empty bags are
                  // consumable again, so Mark empty would lift the quarantine.
                  disabled={[LotStatus.EMPTY, LotStatus.QUARANTINED].includes(lot.status)}
                  onClick={() => setActiveAction('markEmpty')}
                >
                  Mark empty
                </Button>
                <span className="lot-danger-button">
                  {/* Enabled even when this lot is already Quarantined —
                      its downstream may not be; the modal disables
                      itself when the whole graph already is. */}
                  <Button onClick={() => setActiveAction('quarantine')}>
                    Quarantine
                  </Button>
                </span>
              </SpaceBetween>
            )}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{lot.code}</span>
              <LotKindBadge kind={lot.kind} />
              <LotStatusBadge status={lot.status} />
            </div>
          </Header>
        )}
      >
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Qty (remaining / initial)</Box>
            <Box fontSize="heading-xl">{`${lot.remainingQty} / ${lot.initialQty}`}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Installed on (direct)</Box>
            <Box fontSize="heading-xl">{`${usedInTravelers.length} travelers`}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Made from</Box>
            <Box fontSize="heading-xl">{`${lot.sourceLotIds?.length || 0} lots`}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Age</Box>
            <Box fontSize="heading-xl">{ageInDays(lot._createdBy?.timestamp)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Item</Box>
            <Box>
              <PPartDisplay
                fieldName="fullId"
                projectDocId={lot.projectDocId}
                pPartDocId={lot.pPartDocId}
                fallbackMessage="—"
              />
            </Box>
            <Button
              variant="inline-link"
              onClick={() => navigate(`/lots?projectDocId=${lot.projectDocId}&pPartDocId=${lot.pPartDocId}`)}
            >
              View this item&apos;s lots
            </Button>
          </div>
          <div>
            <Box variant="awsui-key-label">Material</Box>
            <Box>{lot.material || '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Created</Box>
            <Box>
              {lot._createdBy?.timestamp?.toDate?.().toLocaleString() || '—'}
              {lot._createdBy?.email ? ` by ${lot._createdBy.email}` : ''}
            </Box>
          </div>
          {lot.kind === LotKinds.INHOUSE && (
            <div>
              <Box variant="awsui-key-label">Machine</Box>
              <Box>{lot.machine || '—'}</Box>
            </div>
          )}
          {/* Tool fields exist only on in-house lots created after they
              were introduced — hide the rows entirely on older lots. */}
          {(lot.aSideTool || lot.bSideTool) && (
            <div>
              <Box variant="awsui-key-label">A / B side tool</Box>
              <Box>{[lot.aSideTool, lot.bSideTool].map((tool) => tool || '—').join(' / ')}</Box>
            </div>
          )}
          {lot.kind === LotKinds.PURCHASED && (
            <div>
              <Box variant="awsui-key-label">Supplier / Order #</Box>
              <Box>{[lot.supplier, lot.orderNumber].filter(Boolean).join(' / ') || '—'}</Box>
            </div>
          )}
        </ColumnLayout>
      </Container>

      <LotInspectionsCard
        inspections={inspections}
        onRecord={() => setActiveAction('recordInspection')}
      />

      <Container header={<Header variant="h2">Genealogy</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Made from</Box>
            {lot.sourceLotIds?.length ? (
              <SpaceBetween direction="vertical" size="xs">
                {lot.sourceLotIds.map((sourceLotId) => (
                  <LotDisplay key={sourceLotId} lotId={sourceLotId} link showPart showStatus />
                ))}
              </SpaceBetween>
            ) : <Box color="text-status-inactive">Nothing — original bag</Box>}
          </div>
          <div>
            <Box variant="awsui-key-label">Used in</Box>
            {usedInLots.length === 0 && usedInTravelers.length === 0 && (
              <Box color="text-status-inactive">Not consumed anywhere yet</Box>
            )}
            <SpaceBetween direction="vertical" size="xs">
              {usedInLots.map((usedLot) => (
                <LotDisplay key={usedLot.id} lot={usedLot} link showPart showStatus />
              ))}
              {usedInTravelers.map((traveler) => (
                <Button
                  key={traveler.id}
                  variant="inline-link"
                  onClick={() => navigate(`/travelers/${traveler.id}`)}
                >
                  {`Traveler ${traveler.id}`}
                </Button>
              ))}
            </SpaceBetween>
          </div>
        </ColumnLayout>
      </Container>

      <LotEventsTable events={events} loading={eventsStatus === 'loading'} />

      {activeAction === 'printLabel' && (
        <PrintLotLabelModal lot={lot} onClose={() => setActiveAction(null)} />
      )}
      {activeAction === 'adjust' && (
        <AdjustLotQtyModal
          lot={lot}
          onClose={() => setActiveAction(null)}
          onSuccess={handleActionSuccess}
        />
      )}
      {activeAction === 'markEmpty' && (
        <LotStatusActionModal
          lot={lot}
          action="markEmpty"
          onClose={() => setActiveAction(null)}
          onSuccess={handleActionSuccess}
        />
      )}
      {activeAction === 'quarantine' && (
        <QuarantineLotModal
          lot={lot}
          onClose={() => setActiveAction(null)}
          onSuccess={handleActionSuccess}
        />
      )}
      {activeAction === 'recordInspection' && (
        <RecordLotInspectionModal
          lot={lot}
          onClose={() => setActiveAction(null)}
          onSuccess={() => {
            handleActionSuccess();
            invalidateInspections();
          }}
        />
      )}
    </SpaceBetween>
  );
}
